import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtEUR, fmtDate } from "@/lib/api";
import { shortConcept } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { FileWarning, CheckCircle2, Save, Paperclip, Unlink, ExternalLink, MessageSquare, Euro, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { TxNotesDialog } from "@/components/TxNotesDialog";
import { STATUS, PAYMENT, ConfBadge } from "./shared";
import { CashPayDialog } from "./CashPayDialog";
import { SupplierCreateDialog } from "./SupplierCreateDialog";

export function InvoiceDrawer({ inv, open, onClose, isAdmin, canReconcile, suppliers, expenseCategories, branches,
  editForm, setEditForm, payments, invCandidates, previewUrl, page2Url, activePage, setActivePage,
  onOpenPage2, onSave, onApprove, onDelete, onDetach, onOpenAttachPage, onReconcile, onRefresh, onChanged }) {
  const navigate = useNavigate();
  const [payOpen, setPayOpen] = useState(false);
  const [supOpen, setSupOpen] = useState(false);
  const [notesTxId, setNotesTxId] = useState(null);
  const [obsText, setObsText] = useState("");
  useEffect(() => { setObsText(inv?.observaciones || ""); }, [inv?.id]);
  const canAct = isAdmin || canReconcile;

  const toggleContab = async () => {
    try {
      await api.patch(`/invoices/${inv.id}/gestoria`, { contabilizada: !inv.contabilizada });
      toast.success(inv.contabilizada ? "Marca de contabilizada quitada" : "Marcada como contabilizada");
      onRefresh();
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al actualizar"); }
  };

  const saveObs = async () => {
    try {
      await api.patch(`/invoices/${inv.id}/gestoria`, { observaciones: obsText });
      toast.success("Observaciones guardadas");
      onRefresh();
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
  };

  const assignSupplier = async (v) => {
    try {
      await api.patch(`/invoices/${inv.id}`, { supplier_id: v === "none" ? null : v });
      toast.success("Proveedor actualizado");
      onRefresh();
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al asignar proveedor"); }
  };

  const attachJustificante = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/invoices/${inv.id}/attach-file`, fd);
      toast.success("Justificante adjuntado");
      onRefresh();
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al adjuntar"); }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="invoice-detail-drawer">
        {inv && (
          <>
            <SheetHeader>
              <SheetTitle className="font-heading">Factura {inv.invoice_number || inv.file_name}</SheetTitle>
            </SheetHeader>
            <div className="mt-6 space-y-5">
              <div className="border border-slate-200 rounded-xl p-3 space-y-2.5 bg-slate-50/60" data-testid="invoice-gestoria-block">
                <div className="text-xs text-slate-500">
                  Fecha de registro: <span className="font-medium text-slate-700" data-testid="invoice-fecha-registro">
                    {inv.created_at ? new Date(inv.created_at).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                  </span>
                </div>
                <label className={`flex items-center gap-2 text-sm ${canAct ? "cursor-pointer" : ""}`}>
                  <input type="checkbox" data-testid="invoice-contabilizada-check" className="accent-green-600 w-4 h-4"
                    checked={!!inv.contabilizada} disabled={!canAct} onChange={toggleContab} />
                  <span className="font-medium">Contabilizada</span>
                  {inv.contabilizada && inv.contabilizada_by && (
                    <span className="text-xs text-slate-400" data-testid="invoice-contabilizada-by">
                      por {inv.contabilizada_by} · {inv.contabilizada_at ? new Date(inv.contabilizada_at).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                    </span>
                  )}
                </label>
                <div className="space-y-1">
                  <Label className="text-xs">Observaciones (gestoría)</Label>
                  <div className="flex gap-2">
                    <textarea data-testid="invoice-observaciones" value={obsText} onChange={(e) => setObsText(e.target.value)}
                      disabled={!canAct} rows={2} placeholder="Notas para la gestoría..."
                      className="flex-1 text-sm rounded-md border border-input bg-white px-2 py-1.5 disabled:opacity-60" />
                    {canAct && (
                      <Button size="sm" variant="outline" data-testid="invoice-observaciones-save" onClick={saveObs}
                        disabled={obsText === (inv.observaciones || "")} className="self-end h-8">Guardar</Button>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={(STATUS[inv.status] || {}).cls}>{(STATUS[inv.status] || {}).label}</Badge>
                <Badge variant="outline" className={(PAYMENT[inv.payment_status] || PAYMENT.pendiente).cls}>
                  {(PAYMENT[inv.payment_status] || PAYMENT.pendiente).label}
                </Badge>
                <ConfBadge value={inv.overall_confidence} />
              </div>
              {(inv.validation_issues || []).length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1" data-testid="invoice-issues">
                  {inv.validation_issues.map((iss) => (
                    <div key={iss} className="text-xs text-amber-800 flex gap-1.5"><FileWarning className="w-3.5 h-3.5 shrink-0 mt-0.5" />{iss}</div>
                  ))}
                </div>
              )}

              {isAdmin ? (
                <div className="grid grid-cols-2 gap-3" data-testid="invoice-edit-form">
                  <div className="col-span-2 text-xs text-slate-400 uppercase tracking-wider">Edición (solo admin)</div>
                  <div className="col-span-2 space-y-1"><Label className="text-xs">Proveedor</Label>
                    <div className="flex gap-1.5">
                      <Select value={inv.supplier_id || "none"} onValueChange={assignSupplier}>
                        <SelectTrigger data-testid="edit-supplier" className="h-8"><SelectValue placeholder="Desconocido" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none" data-testid="edit-supplier-none">Desconocido</SelectItem>
                          {suppliers.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`edit-supplier-${s.id}`}>{s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button size="sm" variant="outline" data-testid="supplier-create-open" className="h-8 px-2 shrink-0" title="Crear proveedor nuevo"
                        onClick={() => setSupOpen(true)}>
                        <Plus className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Tipo de documento</Label>
                    <Select value={editForm.doc_kind || "ticket"} onValueChange={(v) => setEditForm({ ...editForm, doc_kind: v })}>
                      <SelectTrigger data-testid="edit-doc-kind" className="h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ticket" data-testid="edit-doc-kind-ticket">Ticket</SelectItem>
                        <SelectItem value="factura" data-testid="edit-doc-kind-factura">Factura fiscal</SelectItem>
                      </SelectContent>
                    </Select></div>
                  <div className="space-y-1"><Label className="text-xs">Nº factura</Label>
                    <Input data-testid="edit-invoice-number" value={editForm.invoice_number} onChange={(e) => setEditForm({ ...editForm, invoice_number: e.target.value })} className="h-8" /></div>
                  <div className="space-y-1"><Label className="text-xs">Fecha factura</Label>
                    <Input data-testid="edit-invoice-date" type="date" value={editForm.invoice_date} onChange={(e) => setEditForm({ ...editForm, invoice_date: e.target.value })} className="h-8" /></div>
                  <div className="space-y-1"><Label className="text-xs">Vencimiento</Label>
                    <Input data-testid="edit-due-date" type="date" value={editForm.due_date} onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })} className="h-8" /></div>
                  <div className="space-y-1"><Label className="text-xs">Sucursal</Label>
                    <Select value={editForm.branch_id || "none"} onValueChange={(v) => setEditForm({ ...editForm, branch_id: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="edit-branch" className="h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin asignar</SelectItem>
                        {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select></div>
                  <div className="space-y-1"><Label className="text-xs">Base imponible</Label>
                    <Input data-testid="edit-base" type="number" step="0.01" value={editForm.base_amount} onChange={(e) => setEditForm({ ...editForm, base_amount: e.target.value })} className="h-8" /></div>
                  <div className="space-y-1"><Label className="text-xs">IVA</Label>
                    <Input data-testid="edit-vat" type="number" step="0.01" value={editForm.vat_amount} onChange={(e) => setEditForm({ ...editForm, vat_amount: e.target.value })} className="h-8" /></div>
                  <div className="space-y-1"><Label className="text-xs">Otros impuestos</Label>
                    <Input data-testid="edit-other-taxes" type="number" step="0.01" value={editForm.other_taxes} onChange={(e) => setEditForm({ ...editForm, other_taxes: e.target.value })} className="h-8" /></div>
                  <div className="space-y-1"><Label className="text-xs">Total</Label>
                    <Input data-testid="edit-total" type="number" step="0.01" value={editForm.total} onChange={(e) => setEditForm({ ...editForm, total: e.target.value })} className="h-8" /></div>
                  <div className="space-y-1"><Label className="text-xs">Categoría</Label>
                    <Select value={editForm.category_id || "none"} onValueChange={(v) => setEditForm({ ...editForm, category_id: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="edit-category" className="h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin categoría</SelectItem>
                        {expenseCategories.map((c) => <SelectItem key={c.id} value={c.id}>{c.group} · {c.name}</SelectItem>)}
                      </SelectContent>
                    </Select></div>
                  <div className="space-y-1"><Label className="text-xs">Estado de pago</Label>
                    <Select value={editForm.payment_status} onValueChange={(v) => setEditForm({ ...editForm, payment_status: v, paid_amount: v === "pagada" ? (editForm.total || 0) : editForm.paid_amount })}>
                      <SelectTrigger data-testid="edit-payment-status" className="h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pendiente">Pendiente</SelectItem>
                        <SelectItem value="parcial">Pago parcial</SelectItem>
                        <SelectItem value="pagada">Pagada</SelectItem>
                      </SelectContent>
                    </Select></div>
                  <div className="space-y-1"><Label className="text-xs">Importe pagado</Label>
                    <Input data-testid="edit-paid-amount" type="number" step="0.01" value={editForm.paid_amount} onChange={(e) => setEditForm({ ...editForm, paid_amount: e.target.value })} className="h-8" /></div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {[["Proveedor", inv.supplier_name_raw], ["CIF/NIF", inv.supplier_tax_id],
                    ["Nº factura", inv.invoice_number], ["Fecha", fmtDate(inv.invoice_date)],
                    ["Vencimiento", fmtDate(inv.due_date)], ["Moneda", inv.currency],
                    ["Base imponible", fmtEUR(inv.base_amount)], ["IVA", fmtEUR(inv.vat_amount)],
                    ["Otros impuestos", fmtEUR(inv.other_taxes)], ["Total", fmtEUR(inv.total)]].map(([k, v]) => (
                    <div key={k}>
                      <div className="text-xs text-slate-500 uppercase tracking-wider">{k}</div>
                      <div className="font-medium mt-0.5">{v || "—"}</div>
                    </div>
                  ))}
                </div>
              )}

              {(inv.lines || []).length > 0 && (
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Líneas</div>
                  <div className="space-y-1.5">
                    {inv.lines.map((l) => (
                      <div key={l.id} className="flex justify-between text-sm border-b border-slate-100 pb-1.5">
                        <span className="text-slate-700">{l.concept}</span>
                        <span className="font-num">{fmtEUR(l.total)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {payments.length > 0 && (
                <div data-testid="invoice-payments">
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Pagos</div>
                  <div className="space-y-1.5">
                    {payments.map((p) => (
                      <div key={p.id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-1.5">
                        <span className="text-slate-700">{fmtDate(p.date)}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs" data-testid={`payment-method-${p.id}`}>
                            {{ efectivo: "Efectivo", tarjeta: "Tarjeta", transferencia: "Transferencia", otro: "Otro" }[p.method] || "Sin especificar"}
                          </Badge>
                          <span className="font-num font-semibold">{fmtEUR(p.amount)}</span>
                          {p.bank_transaction_id ? (
                            <>
                              <Button size="sm" variant="ghost" data-testid={`payment-goto-tx-${p.id}`}
                                className="h-7 text-xs text-[#D9381E] hover:bg-red-50 gap-1 px-2"
                                onClick={() => navigate(`/conciliacion?tx=${p.bank_transaction_id}`)}>
                                <ExternalLink className="w-3.5 h-3.5" /> Ver movimiento
                              </Button>
                              <Button size="sm" variant="ghost" data-testid={`payment-notes-${p.id}`}
                                className="h-7 text-xs gap-1 px-2 text-slate-500 hover:bg-slate-100"
                                onClick={() => setNotesTxId(p.bank_transaction_id)}>
                                <MessageSquare className="w-3.5 h-3.5" /> Notas
                              </Button>
                            </>
                          ) : (
                            <span className="text-xs text-slate-400" data-testid={`payment-manual-${p.id}`}>pago manual</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {canReconcile && invCandidates && (
                <div data-testid="invoice-reconcile-section">
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Conciliar con movimiento (banco/caja)</div>
                  {invCandidates.candidates.length === 0 ? (
                    <div className="text-xs text-slate-400 border border-dashed rounded-lg p-3">
                      No hay movimientos pendientes por {fmtEUR(invCandidates.invoice_pending)} exactos.
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {invCandidates.candidates.map((c) => (
                        <div key={c.id} data-testid={`inv-candidate-${c.id}`} className="flex items-center justify-between gap-2 border rounded-lg px-3 py-2">
                          <div className="min-w-0">
                            <div className="text-sm truncate" title={c.concept}>{shortConcept(c.concept)}</div>
                            <div className="text-xs text-slate-400">{fmtDate(c.date)} · {c.account_name}{c.date_diff_days > 0 ? ` · ${c.date_diff_days}d de la factura` : ""}</div>
                          </div>
                          <Button size="sm" data-testid={`inv-reconcile-${c.id}`} onClick={() => onReconcile(c.id)}
                            className="bg-[#D9381E] hover:bg-[#B91C1C] text-white shrink-0">Conciliar</Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {inv.attached_page && (
                <div className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2 bg-slate-50" data-testid="attached-page-info">
                  <div className="min-w-0">
                    <div className="text-xs text-slate-500 uppercase tracking-wider">Documento de 2 páginas</div>
                    <div className="text-sm truncate" title={inv.attached_page.file_name}>
                      Pág. 2: {inv.attached_page.file_name}
                    </div>
                  </div>
                  {canReconcile && (
                    <Button size="sm" variant="outline" data-testid="detach-page-button" onClick={onDetach}
                      className="text-red-700 border-red-200 hover:bg-red-50 gap-1 shrink-0">
                      <Unlink className="w-3.5 h-3.5" /> Desasociar
                    </Button>
                  )}
                </div>
              )}

              <div data-testid="invoice-preview">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Vista previa</div>
                  {inv.attached_page && (
                    <div className="flex gap-1" data-testid="invoice-page-tabs">
                      <Button size="sm" variant={activePage === 1 ? "default" : "outline"} data-testid="page-tab-1"
                        onClick={() => setActivePage(1)} className="h-7 text-xs">Página 1</Button>
                      <Button size="sm" variant={activePage === 2 ? "default" : "outline"} data-testid="page-tab-2"
                        onClick={onOpenPage2} className="h-7 text-xs">Página 2</Button>
                    </div>
                  )}
                </div>
                {(() => {
                  const isPage2 = activePage === 2 && inv.attached_page;
                  const url = isPage2 ? page2Url : previewUrl;
                  const mime = isPage2 ? inv.attached_page.mime_type : inv.mime_type;
                  if (url) {
                    return mime === "application/pdf" ? (
                      <iframe src={url} title="preview" className="w-full h-96 rounded-lg border border-slate-200" data-testid="preview-frame" />
                    ) : (
                      <img src={url} alt="Factura" className="w-full rounded-lg border border-slate-200" data-testid="preview-image" />
                    );
                  }
                  if (isPage2 || inv.storage_path) {
                    return (
                      <div className="h-24 flex items-center justify-center text-xs text-slate-400 border border-dashed rounded-lg">
                        Cargando vista previa...
                      </div>
                    );
                  }
                  return (
                    <div className="h-24 flex items-center justify-center text-xs text-slate-400 border border-dashed rounded-lg">
                      Sin archivo adjunto
                    </div>
                  );
                })()}
              </div>
              <div className="flex gap-2 pt-2 flex-wrap">
                {canAct && inv.payment_status !== "pagada"
                  && (inv.total || 0) - (inv.paid_amount || 0) > 0.005 && (
                  <Button data-testid="invoice-cash-pay-button" variant="outline" className="gap-2"
                    onClick={() => setPayOpen(true)}>
                    <Euro className="w-4 h-4" /> Pago efectivo
                  </Button>
                )}
                {isAdmin && (
                  <Button data-testid="invoice-save-button" onClick={onSave} className="bg-[#D9381E] hover:bg-[#B91C1C] text-white gap-2">
                    <Save className="w-4 h-4" /> Guardar cambios
                  </Button>
                )}
                {canReconcile && !inv.attached_page && !inv.parent_invoice_id && (
                  <Button data-testid="attach-page-button" variant="outline" onClick={onOpenAttachPage} className="gap-2">
                    <Paperclip className="w-4 h-4" /> Asociar página
                  </Button>
                )}
                <Button data-testid="invoice-approve-button" onClick={onApprove}
                  className="bg-green-700 hover:bg-green-800 text-white gap-2">
                  <CheckCircle2 className="w-4 h-4" /> Validar
                </Button>
                {canAct && (
                  <Button data-testid="invoice-delete-button" onClick={onDelete} variant="outline"
                    className="text-red-700 border-red-200 hover:bg-red-50 gap-2">
                    <Trash2 className="w-4 h-4" /> Eliminar
                  </Button>
                )}
                {inv.storage_path && (
                  <Button variant="outline" onClick={() => previewUrl && window.open(previewUrl, "_blank")}>Ver original</Button>
                )}
                {!inv.storage_path && canAct && (
                  <>
                    <input id="invoice-attach-input" type="file" accept=".pdf,image/*" className="hidden"
                      data-testid="invoice-attach-input" onChange={attachJustificante} />
                    <Button variant="outline" className="gap-2" data-testid="invoice-attach-button"
                      onClick={() => document.getElementById("invoice-attach-input").click()}>
                      <Paperclip className="w-4 h-4" /> Adjuntar justificante
                    </Button>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </SheetContent>

      <CashPayDialog invoice={inv} open={payOpen} onClose={() => setPayOpen(false)}
        onDone={() => { onRefresh(); onChanged(); }} />
      <SupplierCreateDialog invoice={inv} open={supOpen} onClose={() => setSupOpen(false)}
        onCreated={() => { onRefresh(); onChanged(); }} />
      <TxNotesDialog txId={notesTxId} txLabel="" open={!!notesTxId} onClose={() => setNotesTxId(null)} />
    </Sheet>
  );
}
